import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from 'react-apollo'
import Button from '../button'
import NeonPasswordInput from '../forms/password-input'
import styles from './password-reset-form.css'
import { getGQLErrorMsg } from '../../lib/apollo'
import { FORM_VALUES, FORM_MESSAGES } from '../../constants'
import { useMutateAndLoading } from '../../hooks/use-mutate-and-loading'
import RESET_PASSWORD_MUTATION from '../../../graphql/mutations/reset-password.gql'

/**
 * used by PasswordReset.
 * Handles form validation and request mutation resetPassword.
 */
export default function PasswordResetForm({
  email,
  passwordResetToken,
  setIsRequestDone
}) {
  const [errorMessage, setErrorMessage] = useState('')
  const [resetPasswordMutation] = useMutation(RESET_PASSWORD_MUTATION)
  const mutateAndLoading = useMutateAndLoading(resetPasswordMutation)
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, touchedFields, isSubmitting }
  } = useForm({
    defaultValues: {
      password: '',
      passwordConfirmation: ''
    },
    mode: 'onChange'
  })

  const onSubmit = async values => {
    setErrorMessage('')

    try {
      const data = await mutateAndLoading('resetPassword', {
        variables: {
          password: values.password,
          email,
          passwordResetToken
        }
      })

      if (data?.resetPassword) {
        setIsRequestDone(true)
        reset()
      }
    } catch (error) {
      setErrorMessage(getGQLErrorMsg(error))
    }
  }

  const isSubmittingClass = isSubmitting ? styles.isSubmitting : ''

  return (
    <form
      className={`${styles.form} ${isSubmittingClass}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles.formRow}>
        <div className={styles.formRowItem}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: FORM_MESSAGES.required,
              minLength: {
                value: FORM_VALUES.password.min,
                message: FORM_MESSAGES.password.min
              }
            }}
            render={({ field }) => (
              <NeonPasswordInput
                label="Password"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                warning={
                  (field.value || touchedFields?.password) &&
                  errors.password?.message
                }
                isValid={
                  !errorMessage &&
                  Boolean(
                    field.value && touchedFields?.password && !errors.password
                  )
                }
                isInvalid={Boolean(errorMessage)}
                darkTheme
              />
            )}
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formRowItem}>
          <Controller
            name="passwordConfirmation"
            control={control}
            rules={{
              required: FORM_MESSAGES.required,
              validate: value => value === getValues('password') || FORM_MESSAGES.password.match
            }}
            render={({ field }) => (
              <NeonPasswordInput
                label="Confirm password"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                warning={
                  (field.value || touchedFields?.passwordConfirmation) &&
                  errors.passwordConfirmation?.message
                }
                isValid={
                  !errorMessage &&
                  Boolean(
                    field.value &&
                      touchedFields?.passwordConfirmation &&
                      !errors.passwordConfirmation
                  )
                }
                isInvalid={Boolean(errorMessage)}
                darkTheme
              />
            )}
          />
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <div className={styles.saveWrapper}>
          <Button disabled={isSubmitting} text="Reset" />
        </div>
      </div>
      <div className={styles.errorWrapper}>
        <p className={styles.errorMsg}>{errorMessage}</p>
      </div>
    </form>
  )
}

PasswordResetForm.propTypes = {
  email: PropTypes.string.isRequired,
  passwordResetToken: PropTypes.string,
  setIsRequestDone: PropTypes.func.isRequired
}

PasswordResetForm.defaultProps = {
  passwordResetToken: ''
}
